import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, NgForm, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {UiConstants} from '../../../../util/core-utils';
import {Subject} from 'rxjs';
import {ForwardingNgFormRef, LocalFormGroupValidationErrors} from '../../../../lib/util/services';
import {AppValidators} from '../../../../util/app-validators';
import {Angular2Multiselects} from '../../../../util/multiselect';
import {ErrorMessageService} from '../../../../lib/error-message-parser.service';
import {TriggerResource} from "../../../../lib/trigger/trigger-resource.service";
import {Trigger} from "../../../../lib/trigger/trigger.service";

@Component({
  selector: 'app-trigger-import-dialog',
  templateUrl: './trigger-import-dialog.component.html',
  styleUrls: ['./trigger-import-dialog.component.scss']
})
export class TriggerImportDialogComponent implements OnInit {

  UiConstants = UiConstants;
  @ViewChild('f') form: NgForm;
  formGroup: FormGroup;
  private formGroupValidationErrors: LocalFormGroupValidationErrors;

  model: TriggerImportModel = new TriggerImportModel();

  importInProgress: boolean = false;
  importDnDVisible: boolean = true;
  offlineDropdownSettings: Angular2Multiselects.Settings;
  fieldErrors?: string;
  uploadGlobalErrors?: string;
  fileName?: string;
  importResult?: string = undefined;

  constructor(
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private errorMessageService: ErrorMessageService,
    public dialogRef: MatDialogRef<TriggerImportDialogComponent, TriggerImportDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: TriggerImportDialogData
  ) {
    this.formGroup = this.createFormGroup(formBuilder);
    this.formGroupValidationErrors = LocalFormGroupValidationErrors.ofForm(
      this.createForwardingHtmlForm(),
      this.formGroup
    );
  }

  public static openDialog(
    dialog: MatDialog,
    data: TriggerImportDialogData,
    resultCallback: (result?: TriggerImportDialogResult) => void) {
    const dialogRef = dialog.open(TriggerImportDialogComponent, {
      closeOnNavigation: true,
      data: data,
      width: '60%',
      height: 'auto',
      panelClass: 'custom-dialog-container'
    });
    dialogRef.afterClosed().subscribe((result?: TriggerImportDialogResult) => {
      resultCallback(result);
    });
  }

  ngOnInit(): void {
    this.offlineDropdownSettings = new Angular2Multiselects.SettingsBuilder()
      .singleSelection(false)
      .enableSearchFilter(true)
      .enableCheckAll(true)
      .remoteSearch(false)
      .searchBy(['itemName'])
      .translate(true)
      .build();
  }

  closeDialog(success?: boolean) {
    if (success !== undefined) {
      this.dialogRef.close({success: success});
    } else {
      this.dialogRef.close();
    }
  }

  onImportableFileDropped(files: File[]) {
    this.importResult = undefined;
    const file = files[0];
    this.fileName = file.name;
    const fr = new FileReader();
    const subject: Subject<any> = new Subject<any>();
    fr.onloadend = (function (f) {
      return function (e) {
        try {
          const res = JSON.parse(this.result);
          subject.next(res);
        } catch (e) {
          subject.error('');
        }
      };
    })(file);
    fr.readAsText(file);
    subject.asObservable().subscribe((doc: TriggerResource.TriggerImportDocument[]) => {
      if (doc) {
        this.model.document = doc;
        this.importResult = undefined;
        this.importDnDVisible = false;
      } else {
        this.importResult = 'FORMAT_ERROR';
        this.importDnDVisible = true;
      }
    }, error => {
      this.importResult = 'FORMAT_ERROR';
      this.importDnDVisible = true;
    });
  }

  onFileRemoved() {
    this.model.document = undefined;
    this.fileName = undefined;
    this.importResult = undefined;
    this.fieldErrors = undefined;
    this.uploadGlobalErrors = undefined;
    this.importDnDVisible = true;
  }

  importTrigger() {
    if (!this.model.document) {
      return;
    }
    if (this.importResult !== undefined && this.importResult !== 'ERROR') {
      return;
    }
    if (this.hasLocalFieldError()) {
      return;
    }
    this.data.triggerService.importTriggers({
      triggerParentId: this.data.triggerParentId,
      triggers: this.model.documentRequest,
      settings: {
        trigger_ignore_disabled_items: this.model.triggerIgnoreDisabledItems,
        trigger_remove_existing_triggers: this.model.triggerRemoveExistingTriggers,
      }
    }).subscribe(result => {
      this.closeDialog(true);
    }, error => {
      this.importResult = 'ERROR';
      const localizedError = this.errorMessageService.parseError(error);
      this.uploadGlobalErrors = localizedError!.globalErrors.join('<br/>');
      this.fieldErrors = '';
      if (localizedError?.hasFieldErrors) {
        const f = localizedError!.extractFieldErrors();
        for (let key of Object.keys(f)) {
          this.fieldErrors += `${key}: ${f[key].text}<br/>`;
        }
      }
    });
  }

  hasLocalFieldError(formControlName?: string, errorCode?: string): boolean {
    return this.formGroupValidationErrors.hasFieldError(formControlName, errorCode);
  }

  private createForwardingHtmlForm() {
    return new ForwardingNgFormRef({
      formFn: () => {
        return this.form;
      }
    });
  }

  private createFormGroup(fb: FormBuilder): FormGroup {
    return fb.group({
      triggerIgnoreDisabledItems: fb.control(
        {value: this.model.triggerIgnoreDisabledItems},
        []
      ),
      triggerRemoveExistingTriggers: fb.control(
        {value: this.model.triggerRemoveExistingTriggers},
        []
      )
    });
  }

}

export interface TriggerImportDialogResult {
  success: boolean;
}

export interface TriggerImportDialogData {
  triggerParentId: number;
  triggerService: Trigger.TriggerService;
}

class TriggerImportModel {
  triggerIgnoreDisabledItems: boolean = false;
  triggerRemoveExistingTriggers: boolean = false;
  private _document?: TriggerResource.TriggerImportDocument[];

  get document(): TriggerResource.TriggerImportDocument[] | undefined {
    return this._document;
  }

  set document(value: TriggerResource.TriggerImportDocument[] | undefined) {
    this.reset();
    this._document = value;
  }

  get documentRequest(): TriggerResource.TriggerImportDocument[] {
    let document: TriggerResource.TriggerImportDocument[] = JSON.parse(JSON.stringify(this.document));
    return document;
  }


  reset() {
    this.triggerIgnoreDisabledItems = false;
    this.triggerRemoveExistingTriggers = false;
    this._document = undefined;
  }
}
